import { signIn, signOut } from 'aws-amplify/auth'
import { getCurrentAuthSession, runSignOut } from 'utils/authSession'

export async function loginCognito(data) {
  try {
    // v6 signIn returns { isSignedIn, nextStep }; it throws on bad credentials.
    return await signIn({ username: data.email, password: data.password })
  } catch (error) {
    // Unlike v4, v6 signIn throws if a session is already cached. Clear it and
    // retry so a returning user (or a switched account) can sign in cleanly.
    if (error?.name === 'UserAlreadyAuthenticatedException') {
      try {
        await runSignOut(signOut)
        return await signIn({ username: data.email, password: data.password })
      } catch (retryError) {
        return retryError
      }
    }

    return error
  }
}

export const SESSION_ACTIVE = 'active'
export const SESSION_NONE = 'none'
export const SESSION_ENDED = 'ended'
export const SESSION_UNKNOWN = 'unknown'

// Amplify's TokenOrchestrator clears the stored tokens only for errors that mean
// re-authentication is required, and deliberately keeps them for transient ones so
// the caller can retry. Classifying against the same list keeps "the session is
// over" separate from "I could not check the session".
const SESSION_ENDED_ERRORS = [
  'NotAuthorizedException',
  'TokenRevokedException',
  'UserNotFoundException',
  'PasswordResetRequiredException',
  'UserNotConfirmedException',
  'RefreshTokenReuseException'
]

export async function getCognitoSession() {
  try {
    // v6 resolves with no tokens when the store is empty (never signed in or
    // signed out) and also when the refresh token is expired, since Amplify
    // clears the tokens and returns null rather than throwing. Either way the
    // answer is definitive: there is no session to work with.
    const { tokens } = await getCurrentAuthSession()

    return tokens ? { status: SESSION_ACTIVE, tokens } : { status: SESSION_NONE }
  } catch (error) {
    if (SESSION_ENDED_ERRORS.some(name => error?.name?.startsWith?.(name))) {
      return { status: SESSION_ENDED, error }
    }

    // Network, service, throttling or SDK failure. Amplify left the tokens in
    // place, so the session may still be usable and this must not be reported as
    // a logout.
    return { status: SESSION_UNKNOWN, error }
  }
}

export async function logoutCognito() {
  try {
    await runSignOut(signOut)
  } catch (err) {}
}
